import React, { useEffect, useCallback, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { TopNav } from 'shared/components/TopNav/index.js';
import { getUrlToLoginCallbackPage, getUrlToLoginPage } from "../../urls";
import { SpinnerFullPage } from 'shared/components/SpinnerFullPage/index.js';
import { CheckCircleOutline } from "@material-ui/icons";
import { ActionButton } from "components/ActionButton";
import { TextInput } from "components/TextInput";
import { SignupLoginAgreement } from "containers/SignupLoginAgreement";
import antidoteLogoSrc from "images/antidote-logo.png";
import { isLoggedIn, saveUserToken } from "api/auth-storage";
import { login, sendLoginCode } from "api";
import { HttpApiResponseError } from "shared/http-api";
import { executeRecaptcha } from "api/recaptcha";
import { ExpiryCountdown } from "components/ExpiryCountdown";

const SUCCESS_REDIRECT_TIMEOUT = 3000;

const LOGIN_PAGE_URL = getUrlToLoginPage();
const LOGIN_CALLBACK_PAGE_URL = getUrlToLoginCallbackPage();

const UI_STATES = {
  ENTER_PHONE_NUMBER: '1',
  ENTER_CODE: '2',
  SIGN_IN_SUCCESS: '3'
}

export function PhoneLoginPage () {
  const navigate = useNavigate();
  const isAuthenticated = isLoggedIn();
  const [urlSearchParams] = useSearchParams();
  const [uiState, setUiState] = useState(UI_STATES.ENTER_PHONE_NUMBER);
  const [phoneNumber, setPhoneNumber] = useState<null|string>(null);
  const [expiresAt, setExpiresAt] = useState<null|string>(null);

  const loginCallbackPageUrl = LOGIN_CALLBACK_PAGE_URL + '?' + urlSearchParams.toString();

  const redirectAfterLogin = useCallback(async () => {
    navigate(loginCallbackPageUrl)
  }, [loginCallbackPageUrl]);

  const gotoLoginPage = () => {
    navigate(getUrlToLoginPage());
  };

  useEffect(() => {
    if (!isAuthenticated || uiState === UI_STATES.SIGN_IN_SUCCESS) return;
    redirectAfterLogin();
  }, [isAuthenticated]);

  const handleCodeSent = (phoneNumber: string, expiresAt: string) => {
    setPhoneNumber(phoneNumber);
    setExpiresAt(expiresAt);
    setUiState(UI_STATES.ENTER_CODE);
  };

  const handleCodeConfirmed = () => {
    setUiState(UI_STATES.SIGN_IN_SUCCESS);
    setTimeout(redirectAfterLogin, SUCCESS_REDIRECT_TIMEOUT);
  };

  if (uiState === UI_STATES.SIGN_IN_SUCCESS) {
    return (
      <div className="pt-[200] text-center text-xl">
        <div className="text-2xl mb-5">Welcome to Antidote!</div>
        <CheckCircleOutline color="action" style={{ width: '4.8rem', height: '4.8rem' }} />
        <div className="text-lg mt-5">You will be redirected shortly.</div>
      </div>
    )
  }

  if (isAuthenticated) {
    return <SpinnerFullPage />;
  }

  return (
    <div className="px-6 py-4">
      <TopNav onBackClick={gotoLoginPage} />
      <div className="max-w-[350] mx-auto flex flex-col justify-center mt-12 gap-2">
        <img src={antidoteLogoSrc} alt='Antidote Logo' className="w-full" />
        <div className="text-lg mb-4 text-gray-900 font-medium text-center">
          Log in to Antidote
        </div>
        {uiState === UI_STATES.ENTER_PHONE_NUMBER ? <PhoneNumberForm onCodeSent={handleCodeSent} /> : null}
        {uiState === UI_STATES.ENTER_CODE && phoneNumber ? (
          <ConfirmCodeForm
            phoneNumber={phoneNumber}
            onConfirm={handleCodeConfirmed}
          />
        ) : null}
        {expiresAt ? (
          <div className='text-violet-600 font-bold text-sm my-2 text-left'>
            <ExpiryCountdown subject='Code' expiresAt={expiresAt} />
          </div>
        ) : null}
        <div className="my-4 text-gray-500">
          <div className="mb-4">
            <span>Want to login another way? </span>
            <Link
              to={LOGIN_PAGE_URL}
              className="font-bold text-violet-600"
            >
              Go back
            </Link>
          </div>
          <SignupLoginAgreement />
        </div>
      </div>
    </div>
  );
};

function PhoneNumberForm ({
  onCodeSent
} : {
  onCodeSent: (phoneNumber: string, expiresAt: string) => any
}) {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<null|string>(null);

  const canSubmit = phoneNumber.length === 10;

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const recaptchaToken = await executeRecaptcha('send_login_code');
      const { expiresAt } = await sendLoginCode({ phoneNumber, token: recaptchaToken });
      onCodeSent(phoneNumber, expiresAt);
    } catch (err) {
      if (!(err instanceof HttpApiResponseError)) {
        setError('Could not send OTP because of an unknown error');
      } else {
        const response = await err.json();
        const phoneError = response.errors && response.errors.find(e => e.path === 'phoneNumber');
        const tokenError = response.errors && response.errors.find(e => e.path === 'token');
        if (phoneError) {
          setError(phoneError.message);
        } else if (tokenError) {
          setError(tokenError.message);
        }  else if (err.isTooManyRequests) {
          setError(response.message);
        } else {
          setError('Could not send OTP because of an unknown error');
        }
      }
    }
    setSubmitting(false);
  }

  return (
    <form noValidate onSubmit={onSubmit}>
      <div className="text-gray-500 font-medium mb-4">
        A one time password (OTP) will be sent to your phone number.
      </div>
      <TextInput
        name='phoneNumber'
        value={phoneNumber}
        minLength={10}
        maxLength={10}
        placeholder="Enter your phone number"
        onChange={(value) => {
          setError(null);
          setPhoneNumber(value);
        }}
        required
        error={error || undefined}
      />
      <div className="mt-2">
        <ActionButton
          id='submit-phone-number'
          type="submit"
          label="Log In"
          workingLabel="Sending Confirmation Code"
          working={submitting}
          disabled={!canSubmit}
        />
      </div>
    </form>
  )
}

function ConfirmCodeForm ({
  phoneNumber,
  onConfirm
} : {
  phoneNumber: string,
  onConfirm: () => any
}) {
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string|null>(null);
  const canSubmit = code.length === 6 && !error;

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const json = await login({ identifier: phoneNumber, code });
      saveUserToken(json);
      onConfirm();
    } catch (err) {
      if (!(err instanceof HttpApiResponseError)) {
        setError('Could not verify OTP because of an unknown error');
      } else {
        const response = await err.json();
        const codeError = response.errors && response.errors.find(e => e.path === 'code');
        if (!codeError) {
          setError('Could not verify OTP because of an unknown error');
        } else {
          setError(codeError.message);
        }
      }
    }
    setSubmitting(false);
  }

  return (
    <form noValidate onSubmit={onSubmit}>
      <div className="text-gray-500 font-medium mb-4">
        Enter the 6 digit code sent to {phoneNumber}.
      </div>
      <TextInput
        name='code'
        value={code}
        minLength={6}
        maxLength={6}
        placeholder="Enter code"
        onChange={(value) => {
          setError(null);
          setCode(value);
        }}
        required
        error={error || undefined}
        autoComplete="off"
      />
      <div className="mt-2">
        <ActionButton
          type="submit"
          label="Verify Code"
          workingLabel="Verifying"
          working={submitting}
          disabled={!canSubmit}
        />
      </div>
    </form>
  )
}
